import React, { useState } from "react";
import PropTypes from "prop-types";
import { css } from "glamor";
import Color from "color";

import {
  Button,
  Chip,
  Columns,
  Modal,
  Table,
  Tabs,
  Tooltip
} from "@namroodinc/ni-ui";

import { getOrdinalNumber } from "../../utils/index.js";

import styles from "./styles.module.scss";

const sportsCardPropTypes = {
  acronym: PropTypes.string,
  brandBackgroundImage: PropTypes.string,
  brandColor: PropTypes.string,
  brandProfiling: PropTypes.oneOf(["border", "background"]),
  cityFrom: PropTypes.string,
  countryFrom: PropTypes.shape({
    flag: PropTypes.string,
    longName: PropTypes.string,
    shortName: PropTypes.string
  }),
  created: PropTypes.number,
  description: PropTypes.string,
  displayPic: PropTypes.string,
  displayPicSize: PropTypes.oneOf(["small", "medium", "large"]),
  event: PropTypes.shape({
    score: PropTypes.shape({
      home: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      away: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    })
  }),
  events: PropTypes.array,
  headToHead: PropTypes.shape({
    home: PropTypes.object,
    away: PropTypes.object
  }),
  homeOrAway: PropTypes.oneOf(["home", "away"]),
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  longName: PropTypes.string,
  shortName: PropTypes.string,
  otherInfo: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  players: PropTypes.array,
  record: PropTypes.array,
  stadium: PropTypes.shape({
    name: PropTypes.string,
    capacity: PropTypes.number
  }),
  topper: PropTypes.object
};

const sportsCardDefaultProps = {
  acronym: null,
  brandBackgroundImage: null,
  brandColor: "#000000",
  brandProfiling: "border",
  cityFrom: null,
  countryFrom: {
    flag: null,
    longName: null,
    shortName: null
  },
  created: null,
  description: null,
  displayPic: null,
  displayPicSize: "medium",
  event: {
    score: {
      home: 0,
      away: 0
    }
  },
  events: [],
  headToHead: {},
  homeOrAway: "home",
  id: null,
  longName: null,
  shortName: null,
  otherInfo: {},
  players: [],
  record: [],
  stadium: {
    name: null,
    capacity: null
  },
  topper: {}
};

const PlayersSportCard = (props) => {
  return <SportsCard {...props} displayPicSize={"large"} />;
};

const CardHeaderDisplayPic = (props) => {
  const handleImageError = (event) => {
    event.target.src = `${process.env.REACT_APP_CLOUDINARY_BASE_URL}namroodinc.com/players/NOTEAM-Missing.png`;
  };

  return (
    <div
      className={styles.sportsCardHeaderContentsDisplayPic}
      data-display-pic-by-type={props.type}
    >
      <img
        src={props.displayPic}
        alt={props.longName}
        title={props.longName}
        onError={handleImageError} // Attach the onError event handler
      />
    </div>
  );
};

const CardStadium = (props) => {
  return (
    <>
      {props.stadium?.name &&
        `${props.stadium.name}${
          props.stadium?.capacity
            ? ` (${`${props.stadium.capacity.toLocaleString("en-US")}`}), `
            : ", "
        }
        `}
    </>
  );
};

export const FlagFrom = (props) => {
  const flag = props.countryFrom.flag && (
    <img
      className={`${styles.flag} ${props.flagClassName}`}
      src={props.countryFrom.flag}
      alt={`${props.countryFrom.longName}`}
      title={`${props.countryFrom.longName}`}
    />
  );
  return props.flagOnly ? (
    flag
  ) : (
    <div className={styles.sportsCardHeaderContentsFrom}>
      {flag}
      <span className={styles.sportsCardHeaderContentsTitleFromPlace}>
        {props.children}
        {props.countryFrom.shortName && props.countryFrom.shortName}
      </span>
    </div>
  );
};

FlagFrom.propTypes = {
  countryFrom: PropTypes.shape({
    flag: PropTypes.string,
    longName: PropTypes.string,
    shortName: PropTypes.string
  }),
  flagClassName: PropTypes.string,
  flagOnly: PropTypes.bool
};

FlagFrom.defaultProps = {
  countryFrom: {
    flag: null,
    longName: null,
    shortName: null
  },
  flagClassName: "",
  flagOnly: false
};

const CardHeaderTitleFrom = (props) => {
  return (
    <FlagFrom {...props}>
      {props.showStadium && <CardStadium {...props} />}
      {props.cityFrom && `${props.cityFrom}, `}
    </FlagFrom>
  );
};

CardHeaderTitleFrom.propTypes = {
  ...sportsCardPropTypes,
  showStadium: PropTypes.bool
};

CardHeaderTitleFrom.defaultProps = {
  ...sportsCardDefaultProps,
  showStadium: true
};

const CardTopper = (props) => {
  return <CardHeader {...props} />;
};

CardTopper.propTypes = {
  ...sportsCardPropTypes
};

CardTopper.defaultProps = {
  ...sportsCardDefaultProps
};

const CardHeaderContents = (props) => {
  const brandProfilingRule = css({
    "--brand-color": props.brandColor
  });

  return (
    <div
      className={`${styles.sportsCardHeaderContents} ${brandProfilingRule}`}
      data-display-pic-size={props.displayPicSize}
      data-home-or-away={props.homeOrAway}
      data-type={props.type}
    >
      <CardHeaderDisplayPic {...props} />
      <div className={styles.sportsCardHeaderContentsTitle}>
        <span
          className={styles.sportsCardHeaderContentsTitleEntity}
          data-entity-by-type={props.type}
        >
          {props.longName}
        </span>
        {props.content}
      </div>
      {props.type === "scores" && (
        <div
          className={styles.sportsCardHeaderContentsScores}
          data-scores-value={props.event?.score?.score[props.homeOrAway]}
        >
          <Chip
            label={props.event?.score?.score[props.homeOrAway]}
            profiling={"background"}
          />
        </div>
      )}
      {props.record.length > 0 && (
        <div className={styles.sportsCardHeaderContentsRecord}>
          <Columns
            numberOfColumns={props.record.length}
            gap={"md"}
            responsive={false}
          >
            {props.record.map((record, index) => (
              <div data-record-value={record.acronym} key={index}>
                <Chip
                  label={`${record.acronym}${record.value}`}
                  profiling={"background"}
                  size={"small"}
                />
              </div>
            ))}
          </Columns>
        </div>
      )}
      {props.otherInfo.length > 0 && (
        <div className={styles.sportsCardHeaderContentsOtherInfo}>
          <Columns numberOfColumns={1} gap={"md"}>
            {props.otherInfo.map((info, index) => (
              <Chip
                key={index}
                label={`${info.label}: ${info.value}`}
                profiling={"background"}
                size={"small"}
              />
            ))}
          </Columns>
        </div>
      )}
      <CardHeaderTitleFrom {...props} showStadium={props.type !== "scores"} />
    </div>
  );
};

CardHeaderContents.propTypes = {
  ...sportsCardPropTypes,
  content: PropTypes.node
};

CardHeaderContents.defaultProps = {
  ...sportsCardDefaultProps,
  content: null
};

const CardHeader = (props) => {
  const brandColor = Color(props.brandColor);
  const brandColorIsDark = brandColor.isDark();
  const brandProfilingRule = css({
    "--brand-color": props.brandColor,
    ...(props.brandBackgroundImage && {
      backgroundImage: `url(${props.brandBackgroundImage})`
    }),
    ...(props.brandProfiling === "background" && {
      "--text-color": brandColorIsDark ? "#fff" : "#000",
      "--secondary-text-color": brandColorIsDark ? "#fff" : "#000"
    })
  });
  return (
    <div
      className={`${styles.sportsCardHeader}  ${brandProfilingRule}`}
      data-brand-profiling={props.brandProfiling}
    >
      {props.type === "scores" ? (
        <Columns numberOfColumns={1} gap={"md"}>
          <Columns
            numberOfColumns={3}
            gap={"md"}
            verticalAlignment={"center"}
            responsive={false}
          >
            <span className={styles.matchType}>
              {props.event?.matchType?.name}
            </span>
            <span className={styles.matchOutcome}>
              {props.event.score.winType ? (
                <>
                  {" "}
                  <Tooltip
                    position={"bottom"}
                    text={props.event.score.winType?.name}
                  >
                    {props.event.score.winType?.id}
                  </Tooltip>{" "}
                  in Round {props.event.score.round}
                </>
              ) : (
                "TBD"
              )}
            </span>
            <span className={styles.matchDate}>
              <span className={styles.matchDateText}>{props.event.date}</span>
            </span>
          </Columns>
          <Columns numberOfColumns={2} gap={"md"} verticalAlignment={"top"}>
            <CardHeaderContents
              {...{ ...props, ...props.headToHead.home }}
              homeOrAway="home"
              type="scores"
            />
            <CardHeaderContents
              {...{ ...props, ...props.headToHead.away }}
              homeOrAway="away"
              type="scores"
            />
          </Columns>
        </Columns>
      ) : (
        <CardHeaderContents
          {...props}
          content={
            <>
              {props.description && (
                <span className={styles.description}>{props.description}</span>
              )}
            </>
          }
        />
      )}
    </div>
  );
};

CardHeader.propTypes = {
  ...sportsCardPropTypes
};

CardHeader.defaultProps = {
  ...sportsCardDefaultProps
};

const CardBodyPlayers = (props) => {
  const [showMorePlayers, setShowMorePlayers] = useState(false);

  const brandColor = Color(props.brandColor);
  const brandColorHover = brandColor.darken(0.2);
  const brandColorIsDark = brandColor.isDark();

  const buttonBrandProfilingRule = css({
    "--bg-color": props.brandColor,
    "--border-color": props.brandColor,
    "--button-hover-bg-color": brandColorHover,
    "--text-color": brandColorIsDark ? "#fff" : "#000"
  });

  const players = showMorePlayers
    ? props.players
    : props.players.slice(0, props.numberOfPlayersToShow);

  return (
    <div className={styles.sportsCardBodyPlayers}>
      <h5>Players</h5>
      <ul className={styles.sportsCardList}>
        {players.map((player, index) => (
          <li key={index}>
            <span className={styles.player}>
              <span>{player.number}.</span>
              <span>
                {player.shortName} {player.position && `(${player.position})`}
              </span>
              <Modal openModalButtonLabel="View">
                <PlayersSportCard {...player} />
              </Modal>
            </span>
          </li>
        ))}
      </ul>
      <Button
        className={`${styles.stickyButton} ${buttonBrandProfilingRule}`}
        label={`Show ${props.players.length - props.numberOfPlayersToShow} ${
          showMorePlayers ? "less" : "more"
        }`}
        onClick={() => setShowMorePlayers(!showMorePlayers)}
        size={"small"}
      />
    </div>
  );
};

CardBodyPlayers.propTypes = {
  ...sportsCardPropTypes,
  numberOfPlayersToShow: PropTypes.number
};

CardBodyPlayers.defaultProps = {
  ...sportsCardDefaultProps,
  numberOfPlayersToShow: 4
};

const CardBodyEvents = (props) => {
  return (
    <div className={styles.sportsCardBodyEvents}>
      <Tabs
        type={"dropdown"}
        tabs={props.events.map((eventByMatchClass) => ({
          label: eventByMatchClass.matchClass.name,
          content: (
            <div className={styles.events}>
              {eventByMatchClass.events.map((event, index) => {
                const { allFighters } = event;
                const { matchProminence, matchType, score, time } = event.event;
                // const homeRule = css({
                //   "--brand-color": allFighters.home.brandColor
                // });
                // const awayRule = css({
                //   "--brand-color": allFighters.away.brandColor
                // });
                const matchTypeRule = css({
                  backgroundColor: matchType.brandColor,
                  color: Color(matchType.brandColor).isDark() ? "#fff" : "#000"
                });
                return (
                  <div
                    className={styles.event}
                    key={index}
                    {...(Object.keys(matchProminence).length > 0 && {
                      "data-match-prominence": matchProminence.name
                    })}
                  >
                    <div className={styles.eventRow}>
                      <span
                        className={`${styles.eventRowMatchType} ${matchTypeRule}`}
                      >
                        <Tooltip text={matchType.name} position={"right"}>
                          {matchType.acronym}
                        </Tooltip>
                      </span>
                      <span
                        className={styles.eventRowHome}
                        data-scores-value={score.score.home}
                      >
                        <span>{allFighters.home.shortName}</span>
                        <FlagFrom
                          {...allFighters.home}
                          flagClassName={styles.eventRowHomeFlag}
                          flagOnly
                        />
                      </span>
                      <span
                        className={styles.eventRowHomeScore}
                        data-scores-value={score.score.home}
                      >
                        {score.score.home}
                      </span>
                      <span
                        className={styles.eventRowAwayScore}
                        data-scores-value={score.score.away}
                      >
                        {score.score.away}
                      </span>
                      <span
                        className={styles.eventRowAway}
                        data-scores-value={score.score.away}
                      >
                        <span>{allFighters.away.shortName}</span>
                        <FlagFrom
                          {...allFighters.away}
                          flagClassName={styles.eventRowAwayFlag}
                          flagOnly
                        />
                      </span>
                      <span className={styles.eventRowWinType}>
                        {score.winType && (
                          <Tooltip text={score.winType.name}>
                            {score.winType.id}
                          </Tooltip>
                        )}
                      </span>
                      <span className={styles.eventRowRound}>
                        <Tooltip
                          position={"left"}
                          text={
                            <Table
                              columns={["Round", "Time"]}
                              rows={time.map((round) => [
                                round.round,
                                round.time
                              ])}
                            />
                          }
                        >
                          {getOrdinalNumber(score.round)}
                        </Tooltip>
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          )
        }))}
      />
    </div>
  );
};

CardBodyEvents.propTypes = {
  ...sportsCardPropTypes
};

CardBodyEvents.defaultProps = {
  ...sportsCardDefaultProps
};

const SportsCard = (props) => {
  return (
    <div className={styles.sportsCard}>
      {Object.keys(props.topper).length > 0 && <CardTopper {...props.topper} />}
      <CardHeader {...props} />
      <div className={styles.sportsCardBody}>
        {props.children && (
          <div className={styles.sportsCardBodyContent}>{props.children}</div>
        )}
        {props.events.length > 0 && <CardBodyEvents {...props} />}
        {props.players.length > 0 && <CardBodyPlayers {...props} />}
        {Object.keys(props.otherInfo).length > 0 && (
          <div className={styles.sportsCardBodyOtherInfo}>
            <h5>Other Info</h5>
            <Columns numberOfColumns={2}>
              {Object.keys(props.otherInfo).map((key, index) => (
                <div className={styles.otherInfo} key={index}>
                  <span className={styles.otherInfoHeading}>{key}</span>
                  <span>{props.otherInfo[key]}</span>
                </div>
              ))}
            </Columns>
          </div>
        )}
      </div>
    </div>
  );
};

SportsCard.propTypes = {
  ...sportsCardPropTypes,
  children: PropTypes.node,
  numberOfPlayersToShow: PropTypes.number,
  type: PropTypes.oneOf(["teams", "leagues", "players", "scores"]),
  headToHead: PropTypes.shape({
    home: PropTypes.shape(sportsCardPropTypes),
    away: PropTypes.shape(sportsCardPropTypes)
  })
};

SportsCard.defaultProps = {
  ...sportsCardDefaultProps,
  children: null,
  numberOfPlayersToShow: 4,
  type: "teams",
  headToHead: {
    home: {
      ...sportsCardDefaultProps
    },
    away: {
      ...sportsCardDefaultProps
    }
  }
};

export default SportsCard;
